import React, { ReactNode, useEffect, useState } from "react";
import { css } from "@emotion/react";

import { CSS_COLORS } from "../../styles/styles-css-variables";
import { useStore } from "../../store/hooks/use-store";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { usePageContext } from "../../global/page-context";
import { SegmentElement } from "./SegmentElement";
import { CSS_MQ } from "../../styles/breakpoints";
import { SwarovskiOptikSegmentKey } from "@swo/shared";
import { GTMClassHooks } from "../../domain/google-tag-manager/GoogleTagManagerIntegration";
import { useBodyOverflowStyles } from "../../hooks/use-body-overflow-styles-for";
import { FONTS, fontSize } from "../../styles/fonts";
import { type HeaderSegmentSelector, type SegmentChooserTranslation } from "@swo/storefront";
import { T } from "../T";

export const SegmentChooserOverlay: ReactTFC<HeaderSegmentSelector & SegmentChooserTranslation> = props => {
  const store = useStore();
  const { pageBase } = usePageContext();

  useBodyOverflowStyles(store.state.showSegmentSelector, true);

  function closeSelector() {
    store.dispatch({ type: "segment-selector-dismissed" });
  }

  const onSegmentSelected = (segmentKey: SwarovskiOptikSegmentKey) => () => {
    store.dispatch({ type: "user-picked-preferred-segment", value: segmentKey });
  };

  return store.state.showSegmentSelector ? (
    <div
      className={GTMClassHooks.segmentPickerOverlay}
      css={css`
        height: 100dvh;
        width: 100vw;
        position: fixed;
        background-color: rgba(0, 65, 45, 0.9);
        top: 0;
        left: 0;
        z-index: 1000;

        overflow: hidden;
      `}
    >
      <div
        css={css`
          // duplicated to serve as a fallback
          height: 100vh;
          height: 100dvh;

          text-align: center;

          display: grid;
          grid-template-columns: 1fr;

          grid-template-rows: 1fr 1fr auto;
          grid-gap: 10px;
          ${CSS_MQ.atLeastMd} {
            grid-gap: 20px;
            grid-template-rows: 1fr 2fr auto;
          }

          width: 100%;

          justify-content: space-between;
          align-items: center;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            grid-row: 2 / span 1;
            align-self: flex-start;

            display: flex;
            flex-direction: column;
            align-items: center;
          `}
        >
          <div
            css={css`
              max-width: 100%;
              margin-bottom: 50px;
              padding: 0 10px;

              ${CSS_MQ.atLeastMd} {
                max-width: 700px;
              }

              ${CSS_MQ.atLeastLg} {
                max-width: 1200px;
              }
            `}
          >
            <span className="u-color-white u-uppercase" css={[FONTS.MinervaModern500, fontSize.x7]}>
              <T>{props["segment_chooser.headline"]}</T>
            </span>
          </div>
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              grid-gap: 4px;

              padding-left: 4px;
              padding-right: 4px;

              width: 100%;

              ${CSS_MQ.atLeastSm} {
                grid-template-columns: repeat(3, minmax(0, 1fr));
              }

              ${CSS_MQ.atLeastMd} {
                width: 800px;
              }
            `}
          >
            {props.segments.map(segment => {
              return (
                <SegmentElement
                  key={segment.key}
                  onClick={onSegmentSelected(segment.key)}
                  locale={pageBase.locale}
                  segment={segment}
                />
              );
            })}
          </div>
        </div>

        <div
          css={css`
            grid-row: 3 / span 1;

            padding-bottom: 40px;

            display: flex;
            align-items: center;
            flex-direction: column;
          `}
        >
          <button
            onClick={closeSelector}
            className={GTMClassHooks.segmentPickerOverlayClose}
            css={css`
              text-transform: uppercase;
              color: ${CSS_COLORS.White};
              cursor: pointer;
              font-weight: 500;
              &:hover {
                text-decoration: underline;
              }
            `}
          >
            <T>{props["segment_chooser.closeLabel"]}</T>
          </button>
          <span
            className="u-color-white"
            css={css`
              font-weight: 300;
            `}
          >
            <T>{props.closeText}</T>
          </span>
        </div>
      </div>
    </div>
  ) : null;
};
